<template>
  <div>
    <VContractCustomerTabs locale-namespace="substore" :hide-customer-view="!stockOverviewByCustomerIdentifierSupported" @change="init" />

    <article>
      <div id="STOCK" class="container-fluid grayed">
        <div class="container-fixed">
          <section v-if="readOnlyAccess" class="notification offset-top-1">
            <h3 class="notification-heading" v-html="$t('substore.stock_overview_read_only_note')"></h3>
          </section>

          <VMessage v-if="message" v-bind="message" class="offset-top-2" closable @close="message = null" />
        </div>
      </div>

      <transition name="fade" mode="out-in" appear>
        <VSpinner v-if="isLoading" key="spinner" class="offset-top-2 offset-bottom-6" />
        <div v-else-if="!products" key="empty"></div>
        <div v-else key="data">
          <div class="container-fluid grayed">
            <div class="container-fixed">
              <VGrid
                v-for="optionType in ['booked', 'terminated']"
                :key="optionType"
                :items="filteredProductsBy(optionType)"
                :has-any-promo="isInPromotionPeriod"
                :hide-empty="optionType === 'terminated'"
                data-test="bookedSubscriptions"
              >
                <template #label>
                  {{ $t('substore.' + optionType) }}
                </template>
                <template #item="{ item }">
                  <VStockProductItem v-bind="item" :option-type="optionType" :data-test="'template_id_' + item.subscriptionTemplateId" @message="onMessage" />
                </template>
                <template #empty>
                  <div class="h-ctr v-ctr offset-top-3 offset-bottom-3">
                    {{ $t('substore.stockNoProducts') }}
                  </div>
                </template>
              </VGrid>

              <div v-if="!showAllProducts" class="container-fixed">
                <div style="padding-right: 3px; text-align: right">
                  <a href="#" class="text-nowrap" @click.prevent="onShowAllProducts">
                    <FontAwesomeIcon :icon="faCaretDown" />
                    {{ $t('substore.myproducts_show_all_booked_products') }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <div v-if="hasFootnotes" class="container-fixed">
        <VFootnotes :footnotes="footnotes" />
      </div>
    </article>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import Proxy from '@/proxies/SubstoreStockProxy';
import Transformer from '@/transformers/StockProductTransformer';
import Footnotes from '@/utils/Footnotes';
import TitleMixin from '@/mixins/title';
import VContractCustomerTabs from '@/components/UI/Tabs/ContractCustomerTabs';
import VSpinner from '@/components/Spinner';
import VGrid from '@/components/Substore/StockGrid';
import emitter from '@/plugins/emitter';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons/faCaretDown';

export default {
  name: 'Stock',

  components: {
    VContractCustomerTabs,
    VSpinner,
    VStockProductItem: () => import('@/components/Substore/StockProduct/Item'),
    VFootnotes: () => import('@/components/UI/Footnotes'),
    VMessage: () => import('@/components/Message'),
    FontAwesomeIcon,
    VGrid,
  },

  mixins: [TitleMixin],

  props: {
    uniqueSubscriptionId: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      products: null,
      filterTemplateId: null,
      footnotes: null,
      message: null,
      forceShowAllProducts: false,
      faCaretDown,
      isLoading: false,
    };
  },

  computed: {
    ...mapState('system', ['readOnlyAccess', 'isDeeplink', 'deeplinkSubscriptionTemplateId', 'stockOverviewByCustomerIdentifierSupported']),

    ...mapGetters('shared', ['isCustomerTab']),

    showAllProducts() {
      return !this.filteredProductsOnly;
    },

    filteredProductsOnly() {
      if (this.forceShowAllProducts) return false;

      let filtered = [];
      if (this.filterTemplateId) {
        filtered = this.products.filter((product) => product.subscriptionTemplateId === this.filterTemplateId);
      } else if (this.uniqueSubscriptionId) {
        filtered = this.products.filter((product) => this.containsUniqueSubscriptionId(product));
      }

      if (filtered.length === 0 || filtered.length >= this.products.length) {
        return false;
      }

      return filtered;
    },

    filteredProducts() {
      return this.filteredProductsOnly || this.products;
    },

    hasFootnotes() {
      return this.footnotes && this.footnotes.hasItems();
    },

    isInPromotionPeriod() {
      return this.filteredProducts.some((p) => p.productOptions.some((po) => po.inPromotionPeriod));
    },
  },

  async created() {
    if (this.isDeeplink && this.deeplinkSubscriptionTemplateId) {
      this.filterTemplateId = this.deeplinkSubscriptionTemplateId;
      this.$store.dispatch('system/resetDeeplink');
    }
    this.$webtrekk.viewPage('bestandsuebersicht', 'basket');
  },

  methods: {
    async init(tab) {
      this.products = null;
      this.isLoading = true;
      this.footnotes = new Footnotes();

      // TODO: extend the response with product names, so that we can show them in the WebTrekk calls
      const response = await new Proxy().list(tab);

      if (response.head.fault !== null) throw new Error(response.head.fault);
      this.products = Transformer.fetchCollection(response.data.productList, this.footnotes);
      this.isLoading = false;

      if (this.filterTemplateId && this.filteredProducts.length > 0 && this.filteredProducts[0].cntNotTerminated === 1) {
        const firstNonTerminated = this.filteredProducts[0].productOptions.find((o) => o.status !== 'terminated');
        firstNonTerminated.initialExpand = true;
      }
      if (this.filterTemplateId && this.filteredProducts.length === 1) {
        emitter.emit('setBannerPicture', this.filteredProducts[0].images.headerImageMyProductsResourcePath);
        emitter.emit('setBannerHeadline', this.filteredProducts[0].texts.headerTitleMyProducts);
        emitter.emit('setBannerSubheadline', this.filteredProducts[0].texts.headerSubTitleMyProducts);
      }
    },

    onMessage(message) {
      this.message = message;
      if (message?.reload) {
        this.init(this.isCustomerTab ? 'customer' : 'contract');
      }
    },

    containsUniqueSubscriptionId(product) {
      for (let i = 0; i < product.productOptions.length; i += 1) {
        const option = product.productOptions[i];
        if (option.uniqueSubscriptionId === this.uniqueSubscriptionId) {
          option.initialExpand = true;
          return true;
        }
      }
      return false;
    },

    filteredProductsBy(optionType) {
      return this.filteredProducts.filter((p) => (optionType === 'booked' && p.cntNotTerminated) || (optionType === 'terminated' && p.cntTerminated));
    },

    onShowAllProducts() {
      emitter.emit('setBannerPicture', null);
      emitter.emit('setBannerHeadline', null);
      emitter.emit('setBannerSubheadline', null);
      this.forceShowAllProducts = true;
    },
  },
};
</script>
